import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import StatusBadge from '../../components/common/StatusBadge';
import { SkeletonTable } from '../../components/common/Skeleton';
import CopyButton from '../../components/common/CopyButton';
import { ShieldCheck, Network, Search, Eye, Users, Cpu, ArrowUpRight, History } from 'lucide-react';

const ReferralManagement = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({ totalCommissionsPaid: 0, totalPayoutRecords: 0, totalUsers: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'payouts'

  // Tree Explorer Modal state
  const [selectedUserForTree, setSelectedUserForTree] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [activeLevelTab, setActiveLevelTab] = useState(1);

  // Profit History Modal state
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchAdminReferrals = async () => {
    try {
      const res = await axiosClient.get('/referral/admin/reports');
      setReports(res.data.earnings || []);
      setAllUsers(res.data.allUsers || []);
      setSummary(res.data.summary || { totalCommissionsPaid: 0, totalPayoutRecords: 0, totalUsers: 0 });
    } catch (err) {
      console.error('[Admin Referral Reports Error]:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminReferrals();
  }, []);

  const handleOpenTreeExplorer = async (userObj) => {
    setSelectedUserForTree(userObj);
    setTreeLoading(true);
    setTreeData(null);
    setActiveLevelTab(1);

    try {
      const res = await axiosClient.get(`/referral/admin/tree/${userObj._id}`);
      setTreeData(res.data);
    } catch (err) {
      console.error('[Fetch Admin User Tree Error]:', err);
    } finally {
      setTreeLoading(false);
    }
  };

  const handleOpenProfitHistory = async (userObj) => {
    setSelectedUserForHistory(userObj);
    setHistoryLoading(true);
    setHistoryData(null);

    try {
      const res = await axiosClient.get(`/profit/admin/user/${userObj._id}/history`);
      setHistoryData(res.data);
    } catch (err) {
      console.error('[Fetch Admin User Profit History Error]:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const filteredUsers = allUsers.filter(u => {
    return (
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.referralCode?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const filteredReports = reports.filter(r => {
    return (
      r.referrerId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.referrerId?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.sourceUserId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.sourceUserId?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const currentLevelMembers = treeData?.levels ? treeData.levels[`level${activeLevelTab}`] || [] : [];
  const levelRates = treeData?.rates || { level1: 4, level2: 3, level3: 2, level4: 1, level5: 2 };

  return (
    <div className="space-y-6">
      {/* Title Banner */}
      

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Card hoverEffect>
          <span className="text-xs text-[#9CA3AF] font-semibold">Total Platform Users</span>
          <p className="text-2xl font-extrabold text-[#FFFFFF] mt-2">{summary.totalUsers} Users</p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">Registered trading accounts</p>
        </Card>

        <Card hoverEffect>
          <span className="text-xs text-[#9CA3AF] font-semibold">Total Referral Commissions Paid Out</span>
          <p className="text-2xl font-extrabold text-[#10B981] mt-2">${summary.totalCommissionsPaid.toFixed(2)}</p>
          <p className="text-[10px] text-[#9CA3AF] mt-1">Across 5 level tiers</p>
        </Card>

        <Card hoverEffect>
          <span className="text-xs text-[#9CA3AF] font-semibold">Total Payout Transactions</span>
          <p className="text-2xl font-extrabold text-[#F97316] mt-2">{summary.totalPayoutRecords} Logged</p>
          <p className="text-[10px] text-[#10B981] mt-1">Automatic wallet credit audit</p>
        </Card>
      </div>

      {activeTab === 'users' ? (
        /* Users List to Explore Referral Trees */
        <Card title={`Registered Users & Referral Tree Explorer (${filteredUsers.length})`} subtitle="Click 'Explore Referral Tree' to view any user's downline hierarchy & profit sharing breakdown">
          {loading ? (
            <SkeletonTable rows={4} cols={5} />
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-xs text-[#9CA3AF]">No users found matching search query.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Referral Code</th>
                    <th className="py-3 px-4">Wallet Balance</th>
                    <th className="py-3 px-4">Last Profit Sharing</th>
                    <th className="py-3 px-4">Role</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Joined Date</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A] text-xs">
                  {filteredUsers.map((u) => (
                    <tr key={u._id} className="hover:bg-[#1A1A1E] transition-colors">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-bold text-[#FFFFFF]">{u.name}</span>
                          {u.userIdTag && (
                            <span className="inline-flex items-center space-x-1 font-mono text-[10px] text-[#F59E0B]">
                              <span>({u.userIdTag})</span>
                              <CopyButton text={u.userIdTag} />
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                          <span>{u.email}</span>
                          {u.email && <CopyButton text={u.email} />}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 font-mono font-bold text-[#F59E0B]">
                        <div className="flex items-center space-x-1.5">
                          <span>{u.referralCode}</span>
                          {u.referralCode && <CopyButton text={u.referralCode} />}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 font-mono font-extrabold text-[#10B981]">
                        ${(u.walletBalance || 0).toFixed(2)} USD
                      </td>
                      <td className="py-3.5 px-4">
                        {u.lastProfitShare ? (
                          <div className="space-y-1">
                            <div className="flex items-center space-x-1.5 font-mono">
                              <span className="font-bold text-[#F97316]">-${u.lastProfitShare.amount.toFixed(2)}</span>
                              <span className="text-[10px] text-[#F59E0B]">({u.lastProfitShare.rate}%)</span>
                              {u.lastProfitShare.deductedFromWallet ? (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#10B981]/20 text-[#10B981]">🟢 Deducted</span>
                              ) : (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#F59E0B]/20 text-[#F59E0B]">🟡 Pending</span>
                              )}
                            </div>
                            <p className="text-[10px] font-mono text-[#9CA3AF]">
                              {new Date(u.lastProfitShare.timestamp).toLocaleString()}
                            </p>
                            <button
                              onClick={() => handleOpenProfitHistory(u)}
                              className="inline-flex items-center space-x-1 text-[10px] font-bold text-[#3B82F6] hover:text-[#60A5FA] hover:underline pt-0.5"
                            >
                              <History className="w-3 h-3" />
                              <span>View History</span>
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-1">
                            <span className="text-[#9CA3AF] text-[11px] block">No Profit Share Yet</span>
                            <button
                              onClick={() => handleOpenProfitHistory(u)}
                              className="inline-flex items-center space-x-1 text-[10px] font-bold text-[#3B82F6] hover:text-[#60A5FA] hover:underline"
                            >
                              <History className="w-3 h-3" />
                              <span>View History</span>
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="py-3.5 px-4">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] uppercase font-extrabold ${u.role === 'admin' ? 'bg-[#F97316]/20 text-[#F97316]' : 'bg-[#27272A] text-[#FFFFFF]'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <StatusBadge status={u.status} label={u.status} />
                      </td>
                      <td className="py-3.5 px-4 text-[#9CA3AF]">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleOpenTreeExplorer(u)}
                          className="flex items-center space-x-1 ml-auto"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Tree</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        /* Payouts Audit Table */
        <Card title={`Referral Commission Payout Audit (${filteredReports.length})`} subtitle="Complete log of commissions distributed automatically across Level 1 (4%), Level 2 (3%), Level 3 (2%), Level 4 (1%), Level 5 (2%)">
          {loading ? (
            <SkeletonTable rows={4} cols={6} />
          ) : filteredReports.length === 0 ? (
            <div className="p-8 text-center text-xs text-[#9CA3AF]">No payout records match search query.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#27272A] text-[11px] uppercase tracking-wider text-[#9CA3AF]">
                    <th className="py-3 px-4">Referrer (Upline)</th>
                    <th className="py-3 px-4">Source Trader (Downline)</th>
                    <th className="py-3 px-4">Tier Level</th>
                    <th className="py-3 px-4">Rate (%)</th>
                    <th className="py-3 px-4">Amount Paid</th>
                    <th className="py-3 px-4">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A] text-xs">
                  {filteredReports.map((r) => (
                    <tr key={r._id} className="hover:bg-[#1A1A1E] transition-colors">
                      <td className="py-3.5 px-4">
                        <p className="font-bold text-[#FFFFFF]">{r.referrerId?.name || 'Referrer'}</p>
                        <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                          <span>{r.referrerId?.email}</span>
                          {r.referrerId?.email && <CopyButton text={r.referrerId?.email} />}
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <p className="font-bold text-[#FFFFFF]">{r.sourceUserId?.name || 'Trader'}</p>
                        <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                          <span>{r.sourceUserId?.email}</span>
                          {r.sourceUserId?.email && <CopyButton text={r.sourceUserId?.email} />}
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="px-2 py-0.5 rounded-md bg-[#F97316]/20 text-[#F97316] font-bold text-xs">
                          Level {r.level}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 font-bold text-[#F59E0B]">
                        {r.commissionRate}%
                      </td>
                      <td className="py-3.5 px-4 font-bold text-[#10B981]">
                        +${r.commissionAmount.toFixed(2)}
                      </td>
                      <td className="py-3.5 px-4 text-[#9CA3AF]">
                        {new Date(r.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Admin Referral Tree Explorer Modal */}
      {selectedUserForTree && (
        <div className="fixed inset-0 z-50 !mt-0 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[90vh] bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card flex flex-col space-y-4 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A] shrink-0">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF] flex items-center space-x-2">
                  <span>Referral Tree Explorer: {selectedUserForTree.name}</span>
                  <span className="inline-flex items-center space-x-1 font-mono text-xs font-bold text-[#F59E0B]">
                    <span>({selectedUserForTree.referralCode})</span>
                    {selectedUserForTree.referralCode && <CopyButton text={selectedUserForTree.referralCode} />}
                  </span>
                </h3>
                <p className="text-xs text-[#9CA3AF] flex items-center space-x-1 mt-0.5">
                  <span>{selectedUserForTree.email}</span>
                  {selectedUserForTree.email && <CopyButton text={selectedUserForTree.email} />}
                  <span className="text-[#27272A]">|</span>
                  <span>Inspecting downlines & profit sharing breakdown</span>
                </p>
              </div>
              <button onClick={() => setSelectedUserForTree(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold p-1">✕</button>
            </div>

            {treeLoading ? (
              <div className="p-12 text-center text-xs text-[#9CA3AF] space-y-2">
                <div className="w-6 h-6 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin mx-auto" />
                <p>Loading referral tree data...</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-5 pr-1">
                {/* Tree Summary Metrics */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">
                      {activeLevelTab === 1 ? 'Level 1 Direct' : `Level ${activeLevelTab} Downline`}
                    </span>
                    <p className="text-lg font-extrabold text-[#FFFFFF] mt-1">
                      {treeData?.levels ? (treeData.levels[`level${activeLevelTab}`] || []).length : 0} Users
                    </p>
                  </div>
                  <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">Total Downline Team</span>
                    <p className="text-lg font-extrabold text-[#3B82F6] mt-1">{treeData?.summary?.totalTeam || 0} Users</p>
                  </div>
                  <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">Total Referral Earned</span>
                    <p className="text-lg font-extrabold text-[#10B981] mt-1">${treeData?.summary?.totalReferralEarned ? treeData.summary.totalReferralEarned.toFixed(2) : '0.00'}</p>
                  </div>
                </div>

                {/* Dynamic Level Tabs */}
                <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-[#27272A]">
                  {Array.from({ length: treeData?.maxAvailableLevels !== undefined ? treeData.maxAvailableLevels : 5 }, (_, i) => i + 1).map((lvl) => {
                    const count = treeData?.levels ? (treeData.levels[`level${lvl}`] || []).length : 0;
                    const rate = levelRates[`level${lvl}`] || 0;
                    const isActive = activeLevelTab === lvl;

                    return (
                      <button
                        key={lvl}
                        onClick={() => setActiveLevelTab(lvl)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-2 ${
                          isActive
                            ? 'bg-[#F97316] text-[#FFFFFF]'
                            : 'bg-[#09090B] text-[#9CA3AF] hover:bg-[#1A1A1E] border border-[#27272A]'
                        }`}
                      >
                        <span>Level {lvl} ({rate}%)</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${isActive ? 'bg-white/20 text-white' : 'bg-[#27272A] text-[#9CA3AF]'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Level Members & Detailed Profit Sharing Table */}
                {currentLevelMembers.length === 0 ? (
                  <div className="p-6 text-center text-xs text-[#9CA3AF] bg-[#09090B] rounded-2xl border border-[#27272A]">
                    No downline members in Level {activeLevelTab} for this user.
                  </div>
                ) : (
                  <div className="overflow-x-auto bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-[#27272A] text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                          <th className="py-2.5 px-3">Downline Member</th>
                          <th className="py-2.5 px-3">MT5 Accounts</th>
                          <th className="py-2.5 px-3">Closed PnL</th>
                          <th className="py-2.5 px-3">Profit Share Collected</th>
                          <th className="py-2.5 px-3 text-right">Commission Earned (Level {activeLevelTab})</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#27272A] text-xs">
                        {currentLevelMembers.map((m) => (
                          <tr key={m.id} className="hover:bg-[#121215]">
                            <td className="py-3 px-3">
                              <p className="font-bold text-[#FFFFFF]">{m.name}</p>
                              <div className="flex items-center space-x-1 text-[10px] text-[#9CA3AF] mt-0.5">
                                <span>{m.email}</span>
                                {m.email && <CopyButton text={m.email} />}
                              </div>
                            </td>
                            <td className="py-3 px-3 font-semibold text-[#FFFFFF]">
                              {m.mt5Accounts?.length || 0} Connected
                            </td>
                            <td className="py-3 px-3 font-bold text-[#10B981]">
                              +${m.totalPnL ? m.totalPnL.toFixed(2) : '0.00'}
                            </td>
                            <td className="py-3 px-3 font-bold text-[#F97316]">
                              -${m.totalProfitShareDeducted ? m.totalProfitShareDeducted.toFixed(2) : '0.00'}
                            </td>
                            <td className="py-3 px-3 text-right font-bold text-[#10B981]">
                              +${m.totalEarnedFromUser ? m.totalEarnedFromUser.toFixed(2) : '0.00'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin User Profit History Modal */}
      {selectedUserForHistory && (
        <div className="fixed inset-0 z-50 !mt-0 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-4xl max-h-[90vh] bg-[#121215] border border-[#27272A] rounded-3xl p-6 shadow-card flex flex-col space-y-4 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-[#27272A] shrink-0">
              <div>
                <h3 className="text-base font-extrabold text-[#FFFFFF] flex items-center space-x-2">
                  <History className="w-4 h-4 text-[#F97316]" />
                  <span>Profit Sharing History: {selectedUserForHistory.name}</span>
                  {selectedUserForHistory.userIdTag && (
                    <span className="inline-flex items-center space-x-1 font-mono text-xs font-bold text-[#F59E0B]">
                      <span>({selectedUserForHistory.userIdTag})</span>
                      <CopyButton text={selectedUserForHistory.userIdTag} />
                    </span>
                  )}
                </h3>
                <p className="text-xs text-[#9CA3AF] flex items-center space-x-1 mt-0.5">
                  <span>{selectedUserForHistory.email}</span>
                  {selectedUserForHistory.email && <CopyButton text={selectedUserForHistory.email} />}
                  <span className="text-[#27272A]">|</span>
                  <span>All historical PnL sync cycles & wallet deduction records</span>
                </p>
              </div>
              <button onClick={() => setSelectedUserForHistory(null)} className="text-[#9CA3AF] hover:text-white text-sm font-bold p-1">✕</button>
            </div>

            {historyLoading ? (
              <div className="p-12 text-center text-xs text-[#9CA3AF] space-y-2">
                <div className="w-6 h-6 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin mx-auto" />
                <p>Loading user profit history records...</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-5 pr-1">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">Current Wallet Balance</span>
                    <p className="text-lg font-extrabold text-[#10B981] mt-1">${(historyData?.walletBalance || 0).toFixed(2)} USD</p>
                    <p className="text-[9px] text-[#9CA3AF] mt-0.5">Available for deduction</p>
                  </div>

                  <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">Total Closed PnL</span>
                    <p className="text-lg font-extrabold text-[#FFFFFF] mt-1">${(historyData?.summary?.totalUserPnL || 0).toFixed(2)} USD</p>
                    <p className="text-[9px] text-[#9CA3AF] mt-0.5">Across all connected accounts</p>
                  </div>

                  <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">Total Profit Shared</span>
                    <p className="text-lg font-extrabold text-[#F97316] mt-1">${(historyData?.summary?.totalProfitShared || 0).toFixed(2)} USD</p>
                    <p className="text-[9px] text-[#9CA3AF] mt-0.5">Calculated system share</p>
                  </div>

                  <div className="p-3 bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <span className="text-[10px] text-[#9CA3AF] font-bold uppercase">Pending Uncollected</span>
                    <p className={`text-lg font-extrabold mt-1 ${(historyData?.summary?.pendingUncollectedAmount || 0) > 0 ? 'text-[#F59E0B]' : 'text-[#10B981]'}`}>
                      ${(historyData?.summary?.pendingUncollectedAmount || 0).toFixed(2)} USD
                    </p>
                    <p className="text-[9px] text-[#9CA3AF] mt-0.5">Awaiting wallet deposit</p>
                  </div>
                </div>

                {/* History Records Table */}
                {!historyData?.records || historyData.records.length === 0 ? (
                  <div className="p-6 text-center text-xs text-[#9CA3AF] bg-[#09090B] rounded-2xl border border-[#27272A]">
                    No profit sharing records found for this user yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto bg-[#09090B] rounded-2xl border border-[#27272A]">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-[#27272A] text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                          <th className="py-2.5 px-3">Date & Time</th>
                          <th className="py-2.5 px-3">MT5 Account</th>
                          <th className="py-2.5 px-3">Closed PnL</th>
                          <th className="py-2.5 px-3">Profit Share</th>
                          <th className="py-2.5 px-3">User Net Profit</th>
                          <th className="py-2.5 px-3">Wallet Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#27272A] text-xs">
                        {historyData.records.map((rec) => (
                          <tr key={rec._id} className="hover:bg-[#121215]">
                            <td className="py-3 px-3 font-mono text-[11px] text-[#9CA3AF]">
                              {new Date(rec.createdAt).toLocaleString()}
                            </td>
                            <td className="py-3 px-3">
                              <div className="font-bold text-[#FFFFFF] font-mono">
                                #{rec.mt5AccountId?.accountNumber || 'N/A'}
                              </div>
                              <div className="text-[10px] text-[#9CA3AF]">
                                {rec.mt5AccountId?.server || 'System Sync'}
                              </div>
                            </td>
                            <td className="py-3 px-3 font-mono font-bold text-[#10B981]">
                              +${(rec.closedPnL || 0).toFixed(2)}
                            </td>
                            <td className="py-3 px-3 font-mono font-bold text-[#F97316]">
                              -${(rec.profitShareAmount || 0).toFixed(2)}
                              <span className="text-[10px] text-[#F59E0B] ml-1">({rec.profitShareRate}%)</span>
                            </td>
                            <td className="py-3 px-3 font-mono font-bold text-[#FFFFFF]">
                              +${(rec.userNetProfit || 0).toFixed(2)}
                            </td>
                            <td className="py-3 px-3">
                              {rec.deductedFromWallet ? (
                                <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#10B981]/20 text-[#10B981]">
                                  <span>🟢 Deducted from Wallet</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#F59E0B]/20 text-[#F59E0B]" title="User wallet had insufficient balance at sync time. Share will deduct on next deposit.">
                                  <span>🟡 Pending Wallet Deposit</span>
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralManagement;
